<template>
  <article
    class="dataset-table"
    :class="getTableClass"
    @click="closeDropdowns()"
    :style="`min-height: ${newTableHeight}px; z-index: ${getZIndex}`"
  >
    <header
      class="dataset-table__header"
      :class="`dataset-table__header--${formatTitleForClass(title)}`"
      @click="isTableVisible = !isTableVisible"
    >
      <h2 class="dataset-table__heading">
        {{ title }}
        <component :is="reportIcon" color="white" :width="28" :height="28" />
      </h2>
      <IconChevronDown :is-active="isTableVisible" color="charcoal" is-solid />
    </header>
    <div class="dataset-table__table-container" :class="getTableClass">
      <p v-if="data.length <= 0" class="dataset-table__no-data">
        No data found for this section.
      </p>
      <BaseTable
        v-else
        table-caption="THIS IS A VERY GOOD CAPTION!"
        :stickyHeader="false"
        class="dataset-table__table"
      >
        <template #thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th v-if="!isArchived && !isPendingITRApproval">Used In...</th>
            <th v-if="isPendingITRApproval">Date uploaded</th>
            <th v-if="isPendingITRApproval || isArchived"></th>
            <th
              class="dataset-table__dropdown-header"
              v-if="isActive || isActionRequired"
            >
              <span class="dataset-table__dropdown-container">
                <BaseButton
                  class="dataset-table__dropdown-button"
                  color="grey"
                  @click.stop="checkFirstDropdown()"
                >
                  <span class="dataset-table__dropdown-text">Actions</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="8"
                    viewBox="0 0 8 8"
                    fill="none"
                  >
                    <path
                      d="M7.94531 0.554688L4.95117 7.04883L3.9668 7.04883L0.583008 0.554687L2.2168 0.554688L4.41797 4.9502L6.33203 0.554688H7.94531Z"
                      fill="#585B60"
                    />
                  </svg>
                </BaseButton>
                <div
                  class="dataset-table__dropdown-level-one"
                  v-if="showFirstDropdown"
                  ref="level-one-dropdown"
                >
                  <li
                    v-if="!isActionRequired"
                    class="dataset-table__dropdown-item"
                  >
                    <button type="button" @click="deleteDatasets()">
                      Delete Selected
                    </button>
                  </li>
                  <li class="dataset-table__dropdown-item">
                    <button type="button" @click="downloadDatasets()">
                      Download Selected
                    </button>
                  </li>
                  <li
                    v-if="showAnylsis"
                    class="dataset-table__dropdown-item dataset-table__dropdown-action-item"
                  >
                    <button type="button" @click.stop="checkSecondDropdown()">
                      Run DataCast Analysis
                    </button>
                    <div
                      class="dataset-table__dropdown-level-two"
                      v-if="showSecondDropdown"
                      ref="level-two-dropdown"
                    >
                      <li
                        v-if="hasAllianceSubscription"
                        class="dataset-table__dropdown-item"
                      >
                        <button type="button" @click="runAnalysis('Alliance')">
                          Against Alliance indicators
                        </button>
                      </li>
                      <li
                        v-if="hasDataCastEssentialSubscription"
                        class="dataset-table__dropdown-item"
                      >
                        <button type="button" @click="runAnalysis('Essential')">
                          Against all indicators
                        </button>
                      </li>
                      <li
                        v-if="hasDataCastEssentialSubscription"
                        class="dataset-table__dropdown-item dataset-table__dropdown-action-item"
                      >
                        <button
                          type="button"
                          @click.stop="checkThirdDropdown()"
                        >
                          Against favorites
                        </button>
                        <div
                          class="dataset-table__dropdown-level-three"
                          v-if="
                            showThirdDropdown &&
                            hasDataCastEssentialSubscription
                          "
                          ref="level-three-dropdown"
                        >
                          <li
                            v-for="(favorite, index) in favoriteIndicators"
                            class="dataset-table__dropdown-item"
                            :key="index"
                          >
                            <button
                              type="button"
                              @click="runAnalysis('Favorites', favorite)"
                            >
                              {{ favorite }}
                            </button>
                          </li>
                        </div>
                      </li>
                      <li
                        v-if="hasDatacastProSubscription"
                        class="dataset-table__dropdown-item"
                      >
                        <button type="button" @click="runAnalysis('All')">
                          Against all indicators
                        </button>
                      </li>
                      <li
                        v-if="hasDatacastProSubscription"
                        class="dataset-table__dropdown-item"
                      >
                        <button type="button" @click="runAnalysis('TR')">
                          Against Trends Report indicators
                        </button>
                      </li>
                      <li
                        v-if="hasDatacastProSubscription"
                        class="dataset-table__dropdown-item dataset-table__dropdown-action-item"
                      >
                        <button
                          type="button"
                          @click.stop="checkThirdDropdown()"
                        >
                          Against favorites
                        </button>
                        <div
                          class="dataset-table__dropdown-level-three"
                          v-if="showThirdDropdown && hasDatacastProSubscription"
                          ref="level-three-dropdown"
                        >
                          <li
                            v-for="(favorite, index) in favoriteIndicators"
                            class="dataset-table__dropdown-item"
                            :key="index"
                          >
                            <button
                              type="button"
                              @click="runAnalysis('Favorites', favorite)"
                            >
                              {{ favorite }}
                            </button>
                          </li>
                        </div>
                      </li>
                    </div>
                  </li>
                </div>
              </span>
            </th>
            <th
              v-if="!isArchived && !isPendingITRApproval"
              class="dataset-table__action-header dataset-table__action-header--push-right dataset-table__action-header--select-all dataset-table__action-header--set-width"
            >
              <BaseCheckbox
                :name="`dataset-${tableName}-select-all`"
                v-model="selectAll"
              />
            </th>
          </tr>
        </template>
        <template #tbody v-if="data.length > 0">
          <template :key="index" v-for="(dataset, index) in data">
            <tr
              class="dataset-table__body-row dataset-table__body-row--data"
              :class="{
                'dataset-table__body-row--selected': selectedRow === dataset.ID,
                'dataset-table__body-row--hovered': hoveredRow === dataset.ID,
              }"
              @click="selectRowById($event, dataset.ID)"
              @mouseover="hoveredRow = dataset.ID"
              @mouseleave="hoveredRow = null"
            >
              <td class="dataset-table__body-cell">
                {{ dataset.ID }}
              </td>
              <td class="dataset-table__body-cell">
                {{ dataset.Name }}
              </td>
              <td
                v-if="!isArchived && !isPendingITRApproval"
                class="dataset-table__body-cell"
              >
                {{ dataset.UsedIn }}
              </td>
              <td v-if="isPendingITRApproval" class="dataset-table__body-cell">
                {{
                  dataset.DateUploaded
                    ? new Date(dataset.DateUploaded).toLocaleDateString(
                        'en-us',
                        { month: 'numeric', day: 'numeric', year: 'numeric' }
                      )
                    : ''
                }}
              </td>
              <td class="dataset-table__body-cell">
                <div v-if="isPendingITRApproval">
                  <p
                    v-if="deleteingDatasets.includes(dataset.ID)"
                    class="dataset-table__action-status-message-archive"
                  >
                    ARCHIVE REQUEST PENDING
                  </p>
                  <p
                    v-else
                    class="dataset-table__action-status-message-archive"
                  >
                    {{ dataset.PendingITRMessage }}
                  </p>
                </div>
                <div v-else>
                  <div
                    v-if="
                      processingDatasets.includes(dataset.AnalysisStatus.ID) ||
                      deleteingDatasets.includes(dataset.ID) ||
                      downloadingDatasets.includes(dataset.ID)
                    "
                    class="dataset-table__action-status-container"
                  >
                    <p
                      v-if="
                        processingDatasets.includes(dataset.AnalysisStatus.ID)
                      "
                      class="dataset-table__action-status-message"
                    >
                      Processing Analysis
                    </p>
                    <p
                      v-if="
                        deleteingDatasets.includes(dataset.ID) &&
                        !dataset.IsPortalData
                      "
                      class="dataset-table__action-status-message-archive"
                    >
                      ARCHIVE REQUEST PENDING
                    </p>
                    <p
                      v-if="downloadingDatasets.includes(dataset.ID)"
                      class="dataset-table__action-status-message"
                    >
                      Downloading
                    </p>
                    <ProgressDots
                      animation="pulse"
                      v-if="!deleteingDatasets.includes(dataset.ID)"
                    />
                  </div>
                  <div
                    v-if="
                      isActionRequired &&
                      ((dataset.DataProfileRequired && dataset.DOSCode) ||
                        dataset.CompanyProfileRequired ||
                        dataset.NewDataIsRequired)
                    "
                    class="dataset-table__action-button-container"
                  >
                    <BaseButton
                      v-if="dataset.DataProfileRequired && dataset.DOSCode"
                      class="dataset-table__action-button"
                      @click="openDataProfile(dataset)"
                    >
                      UPDATE DATA
                      <br />
                      PROFILE
                    </BaseButton>
                    <BaseButton
                      v-if="dataset.CompanyProfileRequired"
                      class="dataset-table__action-button"
                      @click="promptCompanyProfile()"
                    >
                      UPDATE COMPANY
                      <br />
                      PROFILE
                    </BaseButton>
                    <BaseButton
                      v-if="dataset.NewDataIsRequired"
                      class="dataset-table__action-button extra-padding"
                      @click="editMyData(dataset.encrypted_id)"
                    >
                      UPLOAD DATA
                    </BaseButton>
                  </div>
                </div>
              </td>
              <td
                v-if="
                  !isArchived &&
                  !isPendingITRApproval &&
                  !deleteingDatasets.includes(dataset.ID) &&
                  !dataset.IsReadOnly
                "
                class="dataset-table__body-cell dataset-table__body-cell--select-column dataset-table__body-cell--push-right"
              >
                <BaseCheckbox
                  v-model="selectedReports"
                  :value="dataset.ID"
                  :name="`dataset-${tableName}-select-${dataset.ID}`"
                  class="dataset-table__body-checkbox"
                />
              </td>
              <td v-else-if="dataset.IsReadOnly"></td>
            </tr>
            <tr
              v-show="isActive || isPendingITRApproval || isActionRequired"
              class="dataset-table__body-row dataset-table__body-row--commands"
              :class="{
                'dataset-table__body-row--commands-selected':
                  selectedRow === dataset.ID,
                'dataset-table__body-row--hovered': hoveredRow === dataset.ID,
              }"
              @click="selectRowById($event, dataset.ID)"
              @mouseover="hoveredRow = dataset.ID"
              @mouseleave="hoveredRow = null"
            >
              <td colspan="6">
                <div
                  v-if="isActionRequired"
                  class="dataset-table__action-container"
                  :class="{
                    'dataset-table__action-container--selected':
                      selectedRow === dataset.ID,
                  }"
                >
                  <BaseButton
                    v-if="dataset.CompanyProfileRequired"
                    size="sm"
                    class="dataset-table__action-button"
                    title="This dataset cannot be edited until a new Company Profile is created."
                    color="dark-grey"
                    disabled
                  >
                    <IconEdit />
                  </BaseButton>
                  <BaseButton
                    v-else-if="dataset.DataProfileRequired && dataset.DOSCode"
                    size="sm"
                    class="dataset-table__action-button"
                    title="This dataset cannot be edited, as a new Data Profile is needed for it. You can still view the dataset."
                    color="dark-grey"
                    disabled
                  >
                    <IconEdit />
                  </BaseButton>
                  <BaseButton
                    v-else
                    size="sm"
                    class="dataset-table__action-button"
                    :title="
                      dataset.IsReadOnly
                        ? 'Please contact this dataset\'s Company Administrator to edit this dataset.'
                        : 'Edit Dataset'
                    "
                    color="dark-grey"
                    :disabled="dataset.IsReadOnly"
                    @click="editMyData(dataset.encrypted_id)"
                  >
                    <IconEdit />
                  </BaseButton>
                  <BaseButton
                    size="sm"
                    class="dataset-table__action-button"
                    title="View Dataset"
                    color="dark-grey"
                    @click="viewMyData(dataset.encrypted_id)"
                  >
                    <IconEye />
                  </BaseButton>
                </div>
                <div
                  v-if="isPendingITRApproval"
                  class="dataset-table__action-container"
                  :class="{
                    'dataset-table__action-container--selected':
                      selectedRow === dataset.ID,
                  }"
                >
                  <BaseButton
                    size="sm"
                    class="dataset-table__action-button"
                    :title="
                      dataset.IsReadOnly
                        ? 'Please contact this dataset\'s Company Administrator to edit this dataset.'
                        : 'This dataset cannot be edited while pending approval from ITR Economics'
                    "
                    color="dark-grey"
                    disabled
                  >
                    <IconEdit />
                  </BaseButton>
                  <BaseButton
                    size="sm"
                    class="dataset-table__action-button"
                    title="View Dataset"
                    color="dark-grey"
                    @click="viewMyData(dataset.encrypted_id)"
                  >
                    <IconEye />
                  </BaseButton>
                </div>
                <div
                  v-if="isActive"
                  class="dataset-table__action-container"
                  :class="{
                    'dataset-table__action-container--selected':
                      selectedRow === dataset.ID,
                  }"
                >
                  <BaseButton
                    size="sm"
                    class="dataset-table__action-button"
                    :title="
                      dataset.IsReadOnly
                        ? 'Please contact this dataset\'s Company Administrator to edit this dataset.'
                        : 'Edit Dataset'
                    "
                    color="dark-grey"
                    :disabled="dataset.IsReadOnly"
                    @click="editMyData(dataset.encrypted_id)"
                  >
                    <IconEdit />
                  </BaseButton>
                  <BaseButton
                    size="sm"
                    class="dataset-table__action-button"
                    title="View Dataset"
                    color="dark-grey"
                    @click="viewMyData(dataset.encrypted_id)"
                  >
                    <IconEye />
                  </BaseButton>
                  <BaseButton
                    :disabled="dataset.IsReadOnly"
                    size="sm"
                    class="dataset-table__action-button"
                    title="Replace Dataset"
                    color="dark-grey"
                    @click="
                      replaceMyData(dataset.encrypted_id, dataset.IsPortalData)
                    "
                  >
                    <IconReplace :width="28" :height="28" />
                  </BaseButton>
                  <BaseButton
                    size="sm"
                    class="dataset-table__action-button"
                    title="Download Dataset"
                    color="dark-grey"
                    :disabled="downloadingDatasets.includes(dataset.ID)"
                    @click="downloadDataset(dataset)"
                  >
                    <IconDownload />
                  </BaseButton>
                  <BaseButton
                    size="sm"
                    class="dataset-table__action-button"
                    title="Delete Dataset"
                    color="dark-grey"
                    :disabled="
                      deleteingDatasets.includes(dataset.ID) ||
                      dataset.IsReadOnly
                    "
                    @click="deleteDataset(dataset)"
                  >
                    <IconDelete />
                  </BaseButton>
                </div>
              </td>
            </tr>
          </template>
        </template>
      </BaseTable>
    </div>
    <ProfileDataModal
      v-if="selectedDataProfile"
      :dataset="selectedDataProfile"
      @close="closeProfileDataModal()"
    />
    <ProfileCompanyModal
      ref="company-profile"
      @close="closeCompanyDataModal()"
    />
    <DatasetDeleteDialog ref="confirm-delete-dialog" />
    <BaseDialog ref="confirmation-dialog" />
  </article>
</template>

<script>
  import { shallowRef } from 'vue';
  import IconChevronDown from '../icon/IconChevronDown.vue';
  import IconDelete from '../icon/IconDelete.vue';
  import IconDownload from '../icon/IconDownload.vue';
  import IconEdit from '../icon/IconEdit.vue';
  import IconReplace from '../icon/IconReplace.vue';
  import IconAlertCircle from '../icon/IconAlertCircle';
  import IconCheckmark from '../icon/IconCheckmark';
  import IconLoading from '../icon/IconLoading';
  import IconEye from '../icon/IconEye';
  import BaseCheckbox from '~/components/base/BaseCheckbox.vue';
  import ProgressDots from '~/components/progress/ProgressDots.vue';
  import DatasetDeleteDialog from '~/components/dataset/DatasetDeleteDialog.vue';
  import ProfileDataModal from '../dashboard/my-data/profile/ProfileDataModal.vue';
  import ProfileCompanyModal from '../dashboard/my-data/profile/ProfileCompanyModal.vue';
  import BaseButton from '~/components/base/BaseButton.vue';
  import BaseDialog from '~/components/base/BaseDialog.vue';
  import BaseTable from '~/components/base/BaseTable.vue';

  export default {
    name: 'DatasetTable',
    components: {
      IconChevronDown,
      IconDelete,
      IconDownload,
      IconEdit,
      IconReplace,
      IconCheckmark,
      IconAlertCircle,
      IconLoading,
      IconEye,
      BaseCheckbox,
      ProgressDots,
      DatasetDeleteDialog,
      ProfileDataModal,
      ProfileCompanyModal,
      BaseButton,
      BaseDialog,
      BaseTable,
    },
    setup() {
      const reportIcons = {
        'action-required': shallowRef(IconAlertCircle),
        active: shallowRef(IconCheckmark),
        'pending-itr-approval': shallowRef(IconLoading),
        archived: shallowRef(IconDelete),
      };

      return {
        reportIcons,
      };
    },
    props: {
      authUser: {
        type: Object,
        required: true,
      },
      title: {
        type: String,
        required: true,
      },
      data: {
        type: Array,
        required: false,
        defalut: () => [],
      },
      processingDatasets: {
        type: Array,
        required: false,
        default: () => [],
      },
      deleteingDatasets: {
        type: Array,
        required: false,
        default: () => [],
      },
      subscriptions: {
        type: Array,
        required: false,
        default: [],
      },
      favorites: {
        type: Object,
        required: false,
      },
    },
    data: () => ({
      selectedReports: [],
      selectAll: false,
      isTableVisible: true,
      selectedRow: null,
      tableName: '',
      reportIcon: null,
      downloadingDatasets: [],
      hoveredRow: null,
      selectedDataProfile: null,
      showFirstDropdown: false,
      showSecondDropdown: false,
      showThirdDropdown: false,
      loadingFavorites: true,
      favoriteIndicators: [],
      selectedFavoriteIndicator: '',
      tableHeight: 50,
      newTableHeight: 50,
      isModalOpen: false,
    }),
    created() {
      this.tableName = this.title.toLowerCase().split(' ').join('-');
      this.reportIcon = this.reportIcons[this.tableName];
      this.isTableVisible =
        this.data.length > 0 && this.tableName !== 'archived';
      if (this.favorites && this.favorites.IsValid && this.favorites.Data) {
        this.favorites.Data.forEach((favorite) => {
          this.favoriteIndicators.push(favorite.Text);
        });
      }
    },
    computed: {
      isActionRequired() {
        return this.tableName === 'action-required';
      },
      isActive() {
        return this.tableName === 'active';
      },
      isPendingITRApproval() {
        return this.tableName === 'pending-itr-approval';
      },
      isArchived() {
        return this.tableName === 'archived';
      },
      hasAllianceSubscription() {
        return this.subscriptions.some((item) => item.includes('Alliance'));
      },
      hasDatacastProSubscription() {
        return this.subscriptions.some((item) => item.includes('Datacast'));
      },
      hasDataCastEssentialSubscription() {
        return this.subscriptions.some((item) => item.includes('DataCast'));
      },
      showAnylsis() {
        return (
          (this.hasAllianceSubscription ||
            this.hasDatacastProSubscription ||
            this.hasDataCastEssentialSubscription) &&
          !this.isActionRequired
        );
      },
      getTableClass() {
        if (this.isTableVisible) {
          if (this.data.length < 2) {
            return 'show-overflow';
          }
          return '';
        }
        return 'dataset-table__table-container--minimized';
      },
      getZIndex() {
        return this.isModalOpen ? 1 : 0;
      },
    },
    watch: {
      selectAll(isSelected) {
        if (isSelected) {
          this.selectedReports = this.data.map((dataset) => {
            return dataset.ID;
          });
          return;
        }

        this.selectedReports = [];
      },
      tableHeight(newValue) {
        this.newTableHeight = newValue;
      },
    },
    methods: {
      formatForKey(stringValue) {
        return stringValue.toLowerCase().replace(' ', '-');
      },
      formatTitleForClass(value) {
        return value.toLowerCase().replace(/ /g, '-');
      },
      selectRowById(event, id) {
        if (event.target.classList.contains('dataset-table__body-checkbox'))
          return;
        if (event.target.type === 'button') return;
        this.selectedRow = this.selectedRow === id ? null : id;
      },
      editMyData(encryptedId) {
        // Check if the url is either dashboard, datacast or alliance
        const url = window.location.href;
        let location = 'dashboard';

        if (url.includes('/datacast/datasets')) {
          location = 'datacast';
        } else if (url.includes('/alliance/datasets')) {
          location = 'alliance';
        }

        window.location.href = `/${location}/datasets/edit/${encryptedId}`;
      },
      viewMyData(encryptedId) {
        // Check if the url is either dashboard, datacast or alliance
        const url = window.location.href;
        let location = 'dashboard';

        if (url.includes('/datacast/datasets')) {
          location = 'datacast';
        } else if (url.includes('/alliance/datasets')) {
          location = 'alliance';
        }

        window.location.href = `/${location}/datasets/view/${encryptedId}`;
      },
      replaceMyData: function (id) {
        window.location.href = `/dashboard/datasets/upload/${id}`;
      },
      async confirmDelete(reportsToDelete = []) {
        const datasetsToDelete = this.data.filter((dataset) =>
          reportsToDelete.includes(dataset.ID)
        );
        const consultingList = datasetsToDelete.filter(
          (dataset) => dataset.IsPortalData === false
        );
        const datacastList = datasetsToDelete.filter(
          (dataset) => dataset.IsPortalData === true
        );
        const response = await this.$refs['confirm-delete-dialog'].prompt({
          consultingList,
          datacastList,
        });
        this.isModalOpen = false;
        return response;
      },
      async deleteDataset(datasetToDelete) {
        this.showThirdDropdown = false;
        this.showSecondDropdown = false;
        this.showFirstDropdown = false;
        try {
          this.isModalOpen = true;
          const datasetsToDelete = this.data.filter(
            (dataset) => dataset.ID === datasetToDelete.ID
          );
          const consultingList = datasetsToDelete.filter(
            (dataset) => dataset.IsPortalData === false
          );
          const datacastList = datasetsToDelete.filter(
            (dataset) => dataset.IsPortalData === true
          );
          const response = await this.$refs['confirm-delete-dialog'].prompt({
            consultingList,
            datacastList,
          });
          this.isModalOpen = false;
          if (!response) return;
        } catch (err) {
          // TODO: HANDLE ERROR - This is just silencing the error
        }

        try {
          this.$emit('deleted-dataset', datasetToDelete.ID);
          await axios.post('/dashboard/dataset/delete', {
            dataset_id: datasetToDelete.ID,
            is_portal_data: datasetToDelete.IsPortalData,
            doscode: encodeURIComponent(datasetToDelete.DOSCode),
          });
          this.$emit('deleted-dataset-successfully', datasetToDelete.ID);
        } catch (err) {
          this.$emit('failed-to-deleted-dataset', datasetToDelete.ID);
        }
      },
      async deleteDatasets() {
        this.showThirdDropdown = false;
        this.showSecondDropdown = false;
        this.showFirstDropdown = false;
        if (this.selectedReports.length <= 0) {
          this.isModalOpen = true;
          await this.$refs['confirmation-dialog'].prompt({
            title: 'No Datasets Selected',
            message: 'You did not select any datasets',
            confirmText: 'Ok',
          });
          this.isModalOpen = false;
          return;
        }

        try {
          this.isModalOpen = true;
          const response = await this.confirmDelete(this.selectedReports);
          if (!response) return;
        } catch (err) {
          // TODO: HANDLE ERROR - This is just silencing the error
        }

        this.selectedReports.forEach(async (datasetId) => {
          const dataset = this.data.find((dataset) => dataset.ID === datasetId);
          try {
            this.$emit('deleted-dataset', dataset.ID);
            await axios.post('/dashboard/dataset/delete', {
              dataset_id: datasetId,
              is_portal_data: dataset.IsPortalData,
              doscode: encodeURIComponent(dataset.DOSCode),
            });
            this.$emit('deleted-dataset-successfully', dataset.ID);
          } catch (err) {
            this.$emit('failed-to-deleted-dataset', dataset.ID);
          }
        });
      },
      async downloadDatasets() {
        this.showThirdDropdown = false;
        this.showSecondDropdown = false;
        this.showFirstDropdown = false;
        if (this.selectedReports.length <= 0) {
          this.isModalOpen = true;
          await this.$refs['confirmation-dialog'].prompt({
            title: 'No Datasets Selected',
            message: 'You did not select any datasets',
            confirmText: 'Ok',
          });
          this.isModalOpen = false;
          return;
        }

        this.selectedReports.forEach(async (datasetId) => {
          try {
            const dataset = this.data.find(
              (dataset) => dataset.ID === datasetId
            );
            await this.downloadDataset(dataset);
          } catch (err) {
            // TODO: HANDLE ERROR - This is just silencing the error
          }
        });
        this.selectedReports = [];
      },
      async downloadDataset(dataset) {
        this.showThirdDropdown = false;
        this.showSecondDropdown = false;
        this.showFirstDropdown = false;
        try {
          this.downloadingDatasets.push(dataset.ID);
          const { data } = await axios.post(
            '/dashboard/dataset/download',
            {
              dataset_id: dataset.ID,
              is_portal_data: dataset.IsPortalData,
              doscode: encodeURIComponent(dataset.DOSCode),
            },
            {
              responseType: 'arraybuffer',
            }
          );

          const fileName = dataset.Name
            ? dataset.Name.replaceAll(/[^\w\s]/gi, '').replaceAll(' ', '')
            : `data-export`;
          const fileURL = window.URL.createObjectURL(new Blob([data]));
          const fileLink = document.createElement('a');
          fileLink.id = 'excel_' + dataset.encrypted_id;
          fileLink.href = fileURL;
          fileLink.setAttribute('download', `${fileName}.xlsx`);
          document.body.appendChild(fileLink);
          fileLink.click();

          fileLink.remove();
        } catch (err) {
          // TODO: HANDLE ERROR - This is just silencing the error
        } finally {
          this.downloadingDatasets = this.downloadingDatasets.filter(
            (id) => id !== dataset.ID
          );
        }
      },
      async runAnalysis(value, favorite = '') {
        this.showThirdDropdown = false;
        this.showSecondDropdown = false;
        this.showFirstDropdown = false;
        if (this.selectedReports.length <= 0) {
          this.isModalOpen = true;
          await this.$refs['confirmation-dialog'].prompt({
            title: 'No Datasets Selected',
            message: 'You did not select any datasets',
            confirmText: 'Ok',
          });
          this.isModalOpen = false;
          return;
        }

        this.selectedReports.forEach(async (datasetId) => {
          const dataset = this.data.find((dataset) => dataset.ID === datasetId);
          try {
            if (this.processingDatasets.includes(dataset.AnalysisStatus.ID))
              return;

            const response = await axios.post(
              '/dashboard/dataset/run-analysis',
              {
                dataset_id: datasetId,
                dataset_name: dataset.Name,
                doscode: encodeURIComponent(dataset.DOSCode),
                category: value,
                favorite: favorite ? encodeURIComponent(favorite) : null,
              }
            );

            if (response.data.IsValid) {
              this.$emit('analized-dataset', response.data.Data.ID);
            }
          } catch (err) {
            this.$emit('failed-to-analize-dataset', dataset.AnalysisStatus.ID);
          }
        });
        this.selectedReports = [];
      },
      openDataProfile(dataset) {
        this.selectedDataProfile = dataset;
        this.isModalOpen = true;
      },
      handleCompanyProfileModalClose() {
        this.$emit('updated-company-profile');
        this.promptCompanyProfile();
      },
      async promptCompanyProfile() {
        try {
          this.isModalOpen = true;
          await this.$refs['company-profile'].prompt();
          this.$emit('refresh-datasets');
        } catch (err) {
          // TODO: HANDLE ERROR - This is just silencing the error
        }
      },
      closeProfileDataModal() {
        this.selectedDataProfile = null;
        this.isModalOpen = false;
        this.$emit('refresh-datasets');
      },
      closeCompanyDataModal() {
        this.isModalOpen = false;
        this.$emit('refresh-datasets');
      },
      checkFirstDropdown() {
        if (this.showFirstDropdown) {
          this.showThirdDropdown = false;
          this.showSecondDropdown = false;
          this.showFirstDropdown = false;
          this.updateTableHeight();
          return;
        }
        this.showFirstDropdown = true;
        this.updateTableHeight();
      },
      checkSecondDropdown() {
        if (this.showSecondDropdown) {
          this.showThirdDropdown = false;
          this.showSecondDropdown = false;
          this.updateTableHeight();
          return;
        }
        this.showSecondDropdown = true;
        this.updateTableHeight();
      },
      checkThirdDropdown() {
        this.showThirdDropdown = !this.showThirdDropdown;
        this.updateTableHeight();
      },
      closeDropdowns() {
        this.showThirdDropdown = false;
        this.showSecondDropdown = false;
        this.showFirstDropdown = false;
        this.newTableHeight = 0;
      },
      updateTableHeight() {
        let height = 50;
        if (this.showFirstDropdown) {
          this.$nextTick(() => {
            const levelOne = this.$refs['level-one-dropdown'];
            const heightWithoutPaddingAndBorders = levelOne.clientHeight;
            height += heightWithoutPaddingAndBorders;
            this.tableHeight = height;
          });
        }
        if (this.showSecondDropdown) {
          this.$nextTick(() => {
            const levelTwo = this.$refs['level-two-dropdown'];
            const heightWithoutPaddingAndBorders = levelTwo.clientHeight;
            height += heightWithoutPaddingAndBorders + 25;
            this.tableHeight = height;
          });
        }
        if (this.showThirdDropdown) {
          this.$nextTick(() => {
            const levelThree = this.$refs['level-three-dropdown'];
            const heightWithoutPaddingAndBorders = levelThree.clientHeight;
            height += heightWithoutPaddingAndBorders;
            this.tableHeight = height;
          });
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  :deep(.base-dropdown) {
    // HACK ALERT: THIS IS NEEDED FOR FIREFOX
    // THERE IS A WEIRD ISSUE THAT PREVENTS THE DROPDOWN FROM BEING CLICKABLE
    // WHEN IT IS INSIDE OF A TABLE
    // THE 'th' TAG, WHICH I NAMED '__dropdown-header' NEEDS TO BE RELATIVE AND THEN IT WILL WORK
    position: unset !important;
  }
  .dataset-table.show {
    overflow: visible;
  }
  .dataset-table {
    display: flex;
    flex-direction: column;
    gap: 15px;
    overflow: hidden;
    // z-index: 0;
    th {
      padding: 5px;
    }

    th,
    td {
      text-align: center;
    }

    th.datset-table__dropdown-header {
      text-align: right;
    }

    &__dropdown-header {
      position: relative;
      width: 10%;
    }
    &__dropdown-container {
      position: relative;
      z-index: 10;
    }
    &__dropdown-button {
      padding: 1px 8px;
      z-index: 10;
    }
    &__dropdown-text {
      font-weight: 500;
      margin-right: 8px;
    }
    &__dropdown-level-one {
      color: var(--itr-black);
      background-color: var(--itr-white);
      box-shadow: 1px 3px 5px rgba(24, 25, 26, 0.25);
      padding: 5px;
      position: absolute;
      right: 0px;
    }
    &__dropdown-level-two {
      color: var(--itr-black);
      background-color: var(--itr-white);
      box-shadow: 1px 3px 5px rgba(24, 25, 26, 0.25);
      padding: 5px;
      position: absolute;
      right: 0;
      transform: translateX(2%);
      @include screen-lg {
        top: 0;
        right: unset;
        transform: translateX(-102%);
      }
    }
    &__dropdown-level-three {
      color: var(--itr-black);
      background-color: var(--itr-white);
      box-shadow: 1px 3px 5px rgba(24, 25, 26, 0.25);
      max-height: 30vh;
      overflow-y: scroll;
      padding: 5px;
      position: absolute;
      right: 0;
      transform: translateX(2%);
      @include screen-lg {
        top: 0;
        right: unset;
        transform: translateX(-102%);
      }
    }
    &__dropdown-action-item {
      position: relative;
    }
    &__dropdown-checkbox-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0;
      white-space: nowrap;
      background-color: var(--itr-white);
      &:hover {
        background-color: darken(#fff, 15%);
      }
      span {
        font-weight: 500;
        padding: 5px 10px 5px 5px;
        margin: 0;
        cursor: pointer;
        width: 100%;
        background-color: inherit;
        text-align: left;
        white-space: nowrap;
        white-space: nowrap;
      }
    }
    &__dropdown-item {
      list-style: none;
      min-width: 192px;
      max-width: 100%;
      width: 100%;
      background-color: inherit;
      font-weight: 500;
      button {
        padding: 5px 10px 5px 5px;
        margin: 0;
        cursor: pointer;
        transition: all 300ms ease-in-out;
        width: 100%;
        background-color: var(--itr-white);
        text-align: left;
        white-space: nowrap;
        &:hover {
          background-color: darken(#fff, 15%);
        }
      }
    }
    &__action-dropdown-text {
      font-weight: 500 !important;
    }
    &__header {
      border-top: none;
      border-radius: 8px;
      padding: 5px 24px;
      cursor: pointer;
      z-index: 0;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;

      &--action-required {
        background: linear-gradient(
          90deg,
          #006742 0%,
          rgba(48, 117, 164, 0) 100%
        );
      }
      &--active {
        background: linear-gradient(
          90deg,
          #3075a4 0%,
          rgba(48, 117, 164, 0) 100%
        );
      }
      &--pending-itr-approval {
        background: linear-gradient(
          90deg,
          #183961 0%,
          rgba(48, 117, 164, 0) 100%
        );
      }
      &--archived {
        background: linear-gradient(
          90deg,
          #585b60 0%,
          rgba(255, 255, 255, 0) 100%
        );
      }
    }
    &__heading {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 0;
      margin: 0;
      text-transform: uppercase;
      font-weight: 700;
      font-size: 28px;
      font-family: 'Open Sans', sans-serif;
      color: #fff;
    }
    &__action-header {
      gap: 4px;
      align-items: center;
      padding-right: 12px;

      &--centered {
        text-align: center;
        padding: 12px;
      }
      &--push-right > * {
        margin-left: auto;
      }
      &--select-all {
        padding: 16px 12px !important;
      }
      &--set-width {
        width: 75px;
      }
    }
    &__action-status-container {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
    }
    &__action-status-message {
      font-size: 14px;
      font-weight: 700;
      color: #17355c;
    }
    &__action-status-message-archive {
      font-size: 17px;
      font-style: italic;
      font-weight: 700;
      color: #17355c;
    }
    &__table-container {
      transition: max-height 350ms ease-in-out;
      z-index: 0;

      &--minimized {
        max-height: 50px;
        transition: max-height 350ms ease-in-out;
      }
    }
    &__table {
      :deep(.base-table__tbody) {
        overflow: auto;
        tr:nth-child(4n),
        tr:nth-child(4n-1) {
          background-color: #ffffff;
        }
        tr:nth-child(4n-2),
        tr:nth-child(4n-3) {
          background-color: #f2f2f2;
        }
      }
    }
    &__action-container {
      display: flex;
      justify-content: flex-end;
      width: 100%;
      gap: 8px;
      height: 0;
      padding: 0 12px;
      margin-top: -50px;

      overflow: hidden;

      transition:
        height 300ms linear,
        padding 300ms linear,
        margin-top 300ms linear;

      &--selected {
        margin-top: 0;
        height: 100%;
        padding: 12px 12px;
        transition:
          height 300ms linear,
          padding 300ms linear,
          margin-top 300ms linear;
      }
    }
    &__action-button-container {
      display: flex;
      gap: 2px;
    }
    &__action-button.extra-padding {
      padding-top: 1rem;
      padding-bottom: 1rem;
    }
    &__action-button {
      line-height: 1;
    }
    &__body-row {
      isolation: isolate;
      position: relative;
      overflow: hidden;
      height: fit-content;
      z-index: 0;

      &--selected {
        // background-color: #d2d2d3 !important;
      }
      &--data {
        td {
          z-index: 0;
          height: 0;
        }
      }
      &--commands {
        z-index: 0;
        transition:
          transform 300ms linear,
          visibility 300ms linear;
        border: none;
        overflow: hidden;

        td {
          padding: 0;
          z-index: 0;
          text-align: right;
        }
      }

      &--commands-selected {
        visibility: visible;
        transition:
          transform 300ms linear,
          z-index 0ms linear 300ms,
          visibility 300ms linear;
      }

      &:hover,
      &--hovered {
        background-color: #d2d2d3 !important;
      }
    }
    &__body-cell {
      padding: 16px 12px;

      &--select-column {
        // display: flex;
        // justify-content: flex-end;
      }

      &--centered {
        text-align: center;
        justify-content: center;
        align-items: center;
      }
      &--push-right > * {
        margin-left: auto;
      }
    }
    &__action-button {
      display: flex;
      justify-content: center;
      align-items: center;
      width: fit-content;
      cursor: pointer;
      min-width: 77px;
    }
    &__no-data {
      text-align: center;
      font-weight: 700;
      font-style: italic;
    }
  }

  :deep(.base-dropdown) {
    margin-left: auto;
  }
  :deep(.base-dropdown__button) {
    margin: 0;
  }
  .show-overflow {
    overflow: visible !important;
  }
</style>
